import { z } from 'zod';

// Stupid Zod issue where a string "false" is converted to true (https://github.com/colinhacks/zod/issues/2985)
export const BooleanStringZod = z.union([
  z.literal('true').transform(() => true),
  z.literal('false').transform(() => false),
  z.boolean(),
]);

export const PasswordSchema = z
  .string({ required_error: 'Password is required' })
  .min(8, { message: 'Password must be at least 8 characters' })
  .max(100, { message: 'Password is too long' })
  .regex(/^\S+$/, { message: 'Password must not contain spaces' });

// For existing passwords (login, current password when changing) — no max to preserve
// backwards compatibility with accounts created before the 100-char limit was introduced
export const CurrentPasswordSchema = z
  .string({ required_error: 'Password is required' })
  .min(1, { message: 'Password is required' });

// Helper function to properly capitalize names
const capitalizeName = (value: string): string => {
  const cleaned = value
    .trim() // Remove leading/trailing whitespace
    .replace(/\s+/g, ' '); // Replace multiple spaces with single space

  // If the entire string is lowercase or uppercase, apply proper capitalization
  if (cleaned === cleaned.toLowerCase() || cleaned === cleaned.toUpperCase()) {
    return cleaned
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  // Otherwise, preserve the original casing
  return cleaned;
};

export const FirstNameSchema = z
  .string({ required_error: 'First name is required' })
  .min(2, { message: 'First name is too short' })
  .max(40, { message: 'First name is too long' })
  .transform(capitalizeName);

export const MiddleNameSchema = z
  .string()
  .min(2, { message: 'Middle name is too short' })
  .max(40, { message: 'Middle name is too long' })
  .transform(capitalizeName);

export const LastNameSchema = z
  .string({ required_error: 'Last name is required' })
  .min(2, { message: 'Last name is too short' })
  .max(40, { message: 'Last name is too long' })
  .transform(capitalizeName);

export const EmailSchema = z
  .string({ required_error: 'Email is required' })
  .email({ message: 'Email is invalid' })
  .min(3, { message: 'Email is too short' })
  .max(100, { message: 'Email is too long' })
  // users can type the email with any casing, but we store it in lowercase
  .transform((value) => value.toLowerCase());

export const SSNSchema = z
  .string({ required_error: 'SSN is required' })
  // SSN is 9 digits, but we allow two dashes somewhere in the string
  .min(11, { message: 'SSN must be 9 digits' })
  .max(11, { message: 'SSN must be 9 digits' });

export const SSNLast4Schema = z
  .string({ required_error: 'Last 4 of SSN is required' })
  .regex(/^\d{4}$/, { message: 'Last 4 of SSN must be 4 digits' });

export const PasswordAndConfirmPasswordSchema = z
  .object({ password: PasswordSchema, confirmPassword: PasswordSchema })
  .superRefine(({ confirmPassword, password }, ctx) => {
    if (confirmPassword !== password) {
      ctx.addIssue({
        path: ['confirmPassword'],
        code: 'custom',
        message: 'The passwords must match',
      });
    }
  });

export const ProfileFormSchema = z.object({
  firstName: FirstNameSchema.optional(),
  lastName: LastNameSchema.optional(),
  streetAddress1: z.string().optional(),
  streetAddress2: z
    .string()
    .optional()
    .nullable()
    .transform((value) => value ?? null), // handle deleting a value after it was already saved
  city: z.string().optional(),
  state: z.string().optional(),
  postalCode: z.string().max(5, { message: 'Must be 5 or fewer characters long' }).optional(),
});

export const CardShippingAddressSchema = z.object({
  streetAddress1: z
    .string({ required_error: 'Street address is required' })
    .min(1, { message: 'Street address is required' }),
  streetAddress2: ProfileFormSchema.shape.streetAddress2, // optional / nullable
  city: z.string({ required_error: 'City is required' }).min(1, { message: 'City is required' }),
  state: z.string({ required_error: 'State is required' }).min(1, { message: 'State is required' }),
  postalCode: z
    .string({ required_error: 'Zip code is required' })
    .min(1, { message: 'Zip code is required' })
    .max(5, { message: 'Must be 5 or fewer characters long' }),
});

// User data needed to sync from CRM
export const CRMPayloadSchema = z.object({
  firstName: FirstNameSchema,
  lastName: LastNameSchema,
  email: EmailSchema,
  ssn: SSNSchema,
  dateOfBirth: z.string().or(z.date()),
});

export type CRMPayload = z.infer<typeof CRMPayloadSchema>;
